import { AppState, AppStateStatus, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface SessionConfig {
  isAppLockEnabled: boolean;
  isBiometricEnabled: boolean;
  timeoutDuration: number; // in milliseconds
}

class SessionManager {
  private static instance: SessionManager;
  private backgroundTimestamp: number | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  private authFlowSuspendCount = 0;
  private sessionConfig: SessionConfig = {
    isAppLockEnabled: false,
    isBiometricEnabled: false,
    timeoutDuration: 30 * 60 * 1000, // 30 minutes default
  };
  private appStateListener: any;

  private constructor() {
    this.initialize();
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  private initialize() {
    // Listen to app state changes
    if (Platform.OS !== 'web') {
      // Native platforms use AppState
      this.appStateListener = AppState.addEventListener('change', this.handleAppStateChange);
      console.log('SessionManager: Initialized with native app state listener');
    } else {
      // Web platform uses visibility API
      this.initializeWebListeners();
      console.log('SessionManager: Initialized with web visibility listeners');
    }
  }

  private handleAppStateChange = (nextAppState: AppStateStatus) => {
    console.log('SessionManager: App state changed to:', nextAppState);
    
    if (nextAppState === 'background' || nextAppState === 'inactive') {
      this.handleAppGoingToBackground();
    } else if (nextAppState === 'active') {
      this.handleAppComingToForeground();
    }
  };

  private initializeWebListeners() {
    if (Platform.OS === 'web') {
      try {
        // Use global references to avoid TS DOM errors
        const globalWindow = (global as any).window || (globalThis as any).window;
        const globalDocument = (global as any).document || (globalThis as any).document;
        
        if (globalWindow && globalDocument) {
          // Web platform uses visibility API
          const handleVisibilityChange = () => {
            if (globalDocument.hidden) {
              console.log('SessionManager: Web page hidden');
              this.handleAppGoingToBackground();
            } else {
              console.log('SessionManager: Web page visible');
              this.handleAppComingToForeground();
            }
          };

          // Listen for visibility changes
          globalDocument.addEventListener('visibilitychange', handleVisibilityChange);
          
          // Listen for page focus/blur
          globalWindow.addEventListener('focus', () => {
            console.log('SessionManager: Web page focused');
            this.handleAppComingToForeground();
          });
          
          globalWindow.addEventListener('blur', () => {
            console.log('SessionManager: Web page blurred');
            this.handleAppGoingToBackground();
          });

          // Store listeners for cleanup
          this.appStateListener = {
            remove: () => {
              globalDocument.removeEventListener('visibilitychange', handleVisibilityChange);
              globalWindow.removeEventListener('focus', this.handleAppComingToForeground);
              globalWindow.removeEventListener('blur', this.handleAppGoingToBackground);
            }
          };
        }
      } catch (error) {
        console.warn('SessionManager: Web listeners not available:', error);
      }
    }
  }

  /** Prevent background session timeout while OAuth sheets (e.g. Apple Sign-In) are open. */
  suspendSessionTimeout(): void {
    this.authFlowSuspendCount += 1;
    this.clearSessionTimeout();
  }

  resumeSessionTimeout(): void {
    this.authFlowSuspendCount = Math.max(0, this.authFlowSuspendCount - 1);
  }

  /** True while OAuth sheets (Apple/Google) are open — App Lock should stay hidden. */
  isAuthFlowSuspended(): boolean {
    return this.authFlowSuspendCount > 0;
  }

  private handleAppGoingToBackground() {
    if (this.authFlowSuspendCount > 0) {
      return;
    }
    console.log('SessionManager: App going to background');
    this.backgroundTimestamp = Date.now();
    
    // Only start timeout if app lock is NOT enabled
    if (!this.sessionConfig.isAppLockEnabled && !this.sessionConfig.isBiometricEnabled) {
      console.log('SessionManager: Starting Firebase session timeout timer');
      this.startSessionTimeout();
    } else {
      console.log('SessionManager: App lock/biometric enabled, Firebase session will persist');
    }
  }

  private handleAppComingToForeground() {
    console.log('SessionManager: App coming to foreground');
    
    // Clear any existing timeout
    this.clearSessionTimeout();
    
    // Check if session should be maintained
    if (this.backgroundTimestamp) {
      const timeInBackground = Date.now() - this.backgroundTimestamp;
      console.log('SessionManager: Time in background:', timeInBackground, 'ms');
      
      // If app lock is enabled, let local authentication handle access
      if (this.sessionConfig.isAppLockEnabled || this.sessionConfig.isBiometricEnabled) {
        console.log('SessionManager: App lock enabled, Firebase session maintained');
        return;
      }
      
      // If no app lock and timeout exceeded, force logout
      if (timeInBackground > this.sessionConfig.timeoutDuration) {
        console.log('SessionManager: Session timeout exceeded, forcing logout');
        this.forceLogout();
      }
    }
    
    this.backgroundTimestamp = null;
  }

  private startSessionTimeout() {
    this.clearSessionTimeout();
    
    this.timeoutId = setTimeout(() => {
      console.log('SessionManager: Session timeout reached, forcing logout');
      this.forceLogout();
    }, this.sessionConfig.timeoutDuration);
  }

  private clearSessionTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private async forceLogout() {
    try {
      console.log('SessionManager: Force logging out user');
      // Use cross-platform signOut from firebase wrapper
      // Import at top-level for static import
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { signOut } = require('../config/firebase');
      if (signOut) {
        await signOut();
        console.log('SessionManager: User logged out successfully');
      } else {
        console.log('SessionManager: No user to logout');
      }
    } catch (error) {
      console.error('SessionManager: Error during force logout:', error);
    }
  }

  // Public methods
  updateSessionConfig(config: Partial<SessionConfig>) {
    this.sessionConfig = { ...this.sessionConfig, ...config };
    console.log('SessionManager: Updated session config:', this.sessionConfig);
  }

  setAppLockEnabled(enabled: boolean) {
    this.sessionConfig.isAppLockEnabled = enabled;
    console.log('SessionManager: App lock enabled:', enabled);
  }

  setBiometricEnabled(enabled: boolean) {
    this.sessionConfig.isBiometricEnabled = enabled;
    console.log('SessionManager: Biometric enabled:', enabled);
  }

  setTimeoutDuration(duration: number) {
    this.sessionConfig.timeoutDuration = duration;
    console.log('SessionManager: Timeout duration set to:', duration, 'ms');
  }

  getSessionConfig(): SessionConfig {
    return { ...this.sessionConfig };
  }

  // Force immediate logout (for testing or manual triggers)
  async forceLogoutNow() {
    console.log('SessionManager: Manual force logout requested');
    await this.forceLogout();
  }

  // Cleanup
  destroy() {
    if (this.appStateListener) {
      this.appStateListener.remove();
    }
    this.clearSessionTimeout();
    console.log('SessionManager: Destroyed');
  }
}

export default SessionManager;
